import React, { useEffect, useState, useRef, useCallback } from 'react';
import { userAPI, temporaryLocationsAPI, friendNotesAPI, friendGroupsAPI, visibilityAPI, friendsAPI } from '../services/api';
import { User, TemporaryLocation, FriendGroupWithMembers, Friend } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { useRoute } from '../contexts/RouteContext';
import { useAuth } from '../contexts/AuthContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import VisibilityBubble from './VisibilityBubble';
import UserReportModal from './UserReportModal';
import '../pages/FriendProfile.css';

// 4 states from top to bottom: expanded → default → half → minimized → close
type PanelState = 'expanded' | 'default' | 'half' | 'minimized';

interface FriendProfilePanelProps {
  friendId: number;
  onClose: () => void;
  onAddressClick?: (lat: number, lng: number, tempLocationId?: number, isOwnTempLocation?: boolean) => void;
  onHeightChange?: (heightVh: number) => void;
  onListClick?: (listId: number) => void;
  onBack?: () => void;
  canGoBack?: boolean;
}

const FriendProfilePanel: React.FC<FriendProfilePanelProps> = ({
  friendId,
  onClose,
  onAddressClick,
  onHeightChange,
  onListClick,
  onBack,
  canGoBack = false
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const listsDropdownRef = useRef<HTMLDivElement>(null);
  const touchStartTime = useRef(0);

  const { theme } = useTheme();
  const { startRouteToDestination } = useRoute();
  const { user: currentUser } = useAuth();
  const isDesktop = useMediaQuery('(min-width: 769px)');

  // Check if viewing own profile
  const isOwnProfile = friendId === currentUser?.id;

  const [friend, setFriend] = useState<User | null>(null);

  // Visibility state
  const [visibilityLevel, setVisibilityLevel] = useState<number | null>(null);
  const [hasCustomVisibility, setHasCustomVisibility] = useState(false);
  const [tempLocations, setTempLocations] = useState<TemporaryLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Personal note state
  const [personalNote, setPersonalNote] = useState('');
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);

  // Lists state (groups renamed to lists)
  const [myLists, setMyLists] = useState<FriendGroupWithMembers[]>([]);
  const [friendLists, setFriendLists] = useState<{ id: number; name: string; color: string }[]>([]);
  const [showListsDropdown, setShowListsDropdown] = useState(false);
  const [isUpdatingLists, setIsUpdatingLists] = useState(false);

  // Friends list for visibility badges (only load if own profile)
  const [allFriends, setAllFriends] = useState<Friend[]>([]);

  // Report modal
  const [showReportModal, setShowReportModal] = useState(false);

  // Panel state - 4 states from expanded to minimized
  const [panelState, setPanelState] = useState<PanelState>('default');

  // TranslateY values for each state (in vh from top)
  const getTranslateY = useCallback(() => {
    if (isDesktop) return 0;
    switch (panelState) {
      case 'expanded': return 10;
      case 'default': return 25;
      case 'half': return 60;
      case 'minimized': return 75;  // More visible
      default: return 25;
    }
  }, [panelState, isDesktop]);


  // Load friend data
  useEffect(() => {
    const loadFriendProfile = async () => {
      try {
        setLoading(true);
        const { user } = await userAPI.getUserById(friendId);
        setFriend(user);

        // Load temp locations - use different endpoint for own profile
        if (isOwnProfile) {
          const { temporaryLocations } = await temporaryLocationsAPI.getUserTemporaryLocations();
          setTempLocations(temporaryLocations);
        } else {
          const { temporaryLocations } = await temporaryLocationsAPI.getFriendTemporaryLocations(friendId);
          setTempLocations(temporaryLocations);

          const { note } = await friendNotesAPI.getNote(friendId);
          setPersonalNote(note || '');
        }

        // Load lists and friends based on profile type
        if (!isOwnProfile) {
          // For friend profiles: load my lists and friend's lists
          const { groups: myListsList } = await friendGroupsAPI.getUserGroupsWithMembers();
          setMyLists(myListsList);

          const { groups: friendListsList } = await friendGroupsAPI.getGroupsForFriend(friendId);
          setFriendLists(friendListsList.map(g => ({
            id: g.id,
            name: g.name,
            color: g.color || '#4A90E2'
          })));
        } else {
          // For own profile: load my lists and my friends (for visibility badge names)
          const { groups: myListsList } = await friendGroupsAPI.getUserGroupsWithMembers();
          setMyLists(myListsList);

          const { friends } = await friendsAPI.getFriends();
          setAllFriends(friends);
        }

        if (!isOwnProfile && currentUser?.address?.id) {
          try {
            const settings = await visibilityAPI.getSettings('permanent', currentUser.address.id);
            const individualRules = settings?.individual_rules || [];
            const individualRule = individualRules.find(r => r.friend_id === friendId);
            if (individualRule) {
              setVisibilityLevel(individualRule.visibility_level);
              setHasCustomVisibility(true);
            } else {
              setVisibilityLevel(null);
              setHasCustomVisibility(false);
            }
          } catch (err) {
            console.error('Failed to load visibility settings:', err);
          }
        }

        setError('');
      } catch (err) {
        console.error('Failed to load friend profile:', err);
        setError('Failed to load friend profile');
      } finally {
        setLoading(false);
      }
    };

    loadFriendProfile();
  }, [friendId, currentUser?.address?.id, isOwnProfile]);

  // Prevent pull-to-refresh
  useEffect(() => {
    document.body.style.overscrollBehavior = 'none';
    return () => {
      document.body.style.overscrollBehavior = '';
    };
  }, []);

  // Notify parent of height changes
  useEffect(() => {
    if (onHeightChange) {
      if (isDesktop) {
        onHeightChange(100);
      } else {
        // Height visible = 100 - translateY
        const heights: Record<PanelState, number> = {
          expanded: 90,
          default: 75,
          half: 40,
          minimized: 25  // More visible now
        };
        onHeightChange(heights[panelState]);
      }
    }
  }, [panelState, onHeightChange, isDesktop]);

  // Handle visibility change
  const handleVisibilityChange = (newLevel: number | null) => {
    setVisibilityLevel(newLevel);
    setHasCustomVisibility(newLevel !== null);
  };

  // Save note
  const handleSaveNote = async () => {
    try {
      setIsSavingNote(true);
      await friendNotesAPI.saveNote(friendId, personalNote.trim() || null);
      setIsEditingNote(false);
    } catch (error) {
      console.error('Failed to save note:', error);
    } finally {
      setIsSavingNote(false);
    }
  };

  // Lists management
  const isFriendInList = (listId: number) => friendLists.some(g => g.id === listId);

  const handleToggleList = async (listId: number) => {
    try {
      setIsUpdatingLists(true);
      const isInList = isFriendInList(listId);

      if (isInList) {
        await friendGroupsAPI.removeMember(listId, friendId);
        setFriendLists(friendLists.filter(g => g.id !== listId));
      } else {
        await friendGroupsAPI.addMembers(listId, { friend_ids: [friendId] });
        const list = myLists.find(g => g.id === listId);
        if (list) {
          setFriendLists([...friendLists, {
            id: list.id,
            name: list.name,
            color: list.color || '#4A90E2'
          }]);
        }
      }
    } catch (error) {
      console.error('Failed to toggle list:', error);
    } finally {
      setIsUpdatingLists(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (listsDropdownRef.current && !listsDropdownRef.current.contains(event.target as Node)) {
        setShowListsDropdown(false);
      }
    };

    if (showListsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showListsDropdown]);

  // Swipe handling with React events and real-time visual feedback
  const isDragging = useRef(false);
  const startYRef = useRef(0);
  const startStateVhRef = useRef(25);
  const [dragOffsetVh, setDragOffsetVh] = useState(0); // For real-time feedback

  const stateToVh: Record<PanelState, number> = {
    expanded: 10,
    default: 25,
    half: 60,
    minimized: 75  // More visible (was 82)
  };

  // Zone boundaries
  const getStateFromPosition = (positionVh: number): PanelState | 'close' => {
    if (positionVh < 18) return 'expanded';
    if (positionVh < 42) return 'default';
    if (positionVh < 68) return 'half';
    if (positionVh < 85) return 'minimized';  // Easier to close
    return 'close';
  };

  const handleSwipeTouchStart = (e: React.TouchEvent) => {
    if (isDesktop) return;
    startYRef.current = e.touches[0].clientY;
    startStateVhRef.current = stateToVh[panelState];
    touchStartTime.current = Date.now();
    isDragging.current = false;
    setDragOffsetVh(0);
  };

  const handleSwipeTouchMove = (e: React.TouchEvent) => {
    if (isDesktop) return;
    const deltaY = e.touches[0].clientY - startYRef.current;

    if (Math.abs(deltaY) > 8) {
      isDragging.current = true;
      e.preventDefault(); // Block pull-to-refresh

      // Real-time visual feedback - panel follows finger
      const screenHeight = window.innerHeight;
      const deltaVh = (deltaY / screenHeight) * 100;

      // Clamp to valid range (can't go above expanded or too far below close)
      const targetVh = startStateVhRef.current + deltaVh;
      const clampedVh = Math.max(5, Math.min(95, targetVh));
      const offset = clampedVh - stateToVh[panelState];

      setDragOffsetVh(offset);
    }
  };

  const handleSwipeTouchEnd = (e: React.TouchEvent) => {
    if (isDesktop) return;

    // Reset drag offset
    setDragOffsetVh(0);

    if (!isDragging.current) return;

    const touchEndY = e.changedTouches[0].clientY;
    const deltaY = touchEndY - startYRef.current;
    const deltaTime = Math.max(Date.now() - touchStartTime.current, 1);

    const screenHeight = window.innerHeight;
    const deltaVh = (deltaY / screenHeight) * 100;
    const targetVh = startStateVhRef.current + deltaVh;

    const velocity = Math.abs(deltaY / deltaTime);

    // Distinguish between quick flick and long drag
    const isQuickFlick = deltaTime < 200 && velocity > 0.3;
    const isLongDrag = deltaTime >= 200 || Math.abs(deltaVh) > 12;

    let newState: PanelState | 'close';

    if (isQuickFlick && Math.abs(deltaY) > 15) {
      // Quick flick - move ONE state
      const states: (PanelState | 'close')[] = ['expanded', 'default', 'half', 'minimized', 'close'];
      const currentIndex = states.indexOf(panelState);

      if (deltaY > 0) {
        newState = states[Math.min(currentIndex + 1, states.length - 1)];
      } else {
        newState = states[Math.max(currentIndex - 1, 0)] as PanelState;
      }
    } else if (isLongDrag) {
      // Long drag - snap to zone (can skip states)
      newState = getStateFromPosition(targetVh);
    } else {
      newState = panelState;
    }

    if (newState === 'close') {
      onClose();
    } else if (newState !== panelState) {
      setPanelState(newState);
    }

    isDragging.current = false;
  };

  const handleAddressClickInternal = (lat: number, lng: number, tempLocationId?: number) => {
    // Move to half position to show more map
    setPanelState('half');

    if (onAddressClick) {
      // Pass offset to center popup in visible area (above panel)
      // Panel at 'half' covers 40% of screen, so offset map center down by ~20%
      // Pass isOwnProfile to indicate whether this is user's own temp location
      onAddressClick(lat, lng, tempLocationId, isOwnProfile);
    }
  };

  const handleOpenDirections = (lat: number, lng: number, name?: string, address?: string) => {
    startRouteToDestination({
      lat,
      lng,
      name: name || (friend?.first_name ? `${friend?.first_name} ${friend?.last_name}` : undefined),
      address,
      markerType: 'friend',
      profilePhoto: friend?.profile_photo,
      firstName: friend?.first_name,
      lastName: friend?.last_name,
      friendId: friend?.id, // For FriendMarker to know not to render
    });
    onClose(); // Close panel to show full route view
  };

  const getPhotoUrl = (photoPath?: string) => {
    if (!photoPath) return null;
    if (photoPath.startsWith('http')) return photoPath;
    return `${process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:5000'}${photoPath}`;
  };

  if (loading) {
    return (
      <div className="friend-profile-panel" style={{ transform: `translateY(${getTranslateY()}vh)` }}>
        <div className="loading-message">Chargement du profil...</div>
      </div>
    );
  }

  if (error || !friend) {
    return (
      <div className="friend-profile-panel" style={{ transform: `translateY(${getTranslateY()}vh)` }}>
        <div className="error-message">{error || 'Ami introuvable'}</div>
        <button onClick={onClose} className="btn-back">Fermer</button>
      </div>
    );
  }

  // Helper functions for visibility badges
  const getListNames = (groupIds?: number[]): string => {
    if (!groupIds || groupIds.length === 0) return '';
    const names = groupIds
      .map(id => myLists.find(list => list.id === id)?.name)
      .filter(Boolean);
    return names.join(', ');
  };

  const getFriendNames = (friendIds?: number[]): string => {
    if (!friendIds || friendIds.length === 0) return '';
    const names = friendIds
      .map(id => {
        const friend = allFriends.find(f => f.user_id === id);
        return friend ? `${friend.first_name} ${friend.last_name}` : null;
      })
      .filter(Boolean);
    return names.join(', ');
  };

  // Calculate current transform including drag offset
  const currentTranslateY = getTranslateY() + dragOffsetVh;

  return (
    <div
      ref={panelRef}
      className={`friend-profile-panel panel-${panelState} ${isDragging.current ? 'dragging' : ''}`}
      style={{
        transform: `translateY(${currentTranslateY}vh)`,
        transition: dragOffsetVh !== 0 ? 'none' : undefined, // Disable transition while dragging
      }}
      onTouchStart={handleSwipeTouchStart}
      onTouchMove={handleSwipeTouchMove}
      onTouchEnd={handleSwipeTouchEnd}
    >
      {/* Swipe Area - Header with contact info on mobile only */}
      {!isDesktop && (
      <div className="panel-swipe-area">
        {/* Drag Handle Indicator */}
        <div className="panel-drag-handle">
          <div className="handle-indicator"></div>
        </div>

        {/* Contact Header - Inside swipe area on mobile */}
        <div className="profile-header-compact">
          {/* Row 1: Photo + Name + Visibility */}
          <div className="header-row-main">
            <div className="profile-photo-container-small">
              {friend.profile_photo ? (
                <img
                  src={getPhotoUrl(friend.profile_photo) || undefined}
                  alt={`${friend.first_name} ${friend.last_name}`}
                  className="profile-photo-small"
                />
              ) : (
                <div className="profile-photo-placeholder-small">
                  {friend.first_name[0]}{friend.last_name[0]}
                </div>
              )}
            </div>
            <div className="profile-info-compact">
              <h1 className="profile-name-compact">
                {friend.first_name} {friend.last_name}
              </h1>
              {friend.email && (
                <p className="profile-phone-compact">{friend.email}</p>
              )}
              {friend.phone && (
                <p className="profile-phone-compact">{friend.phone}</p>
              )}
            </div>
            {!isOwnProfile && currentUser?.address?.id && (
              <VisibilityBubble
                friendId={friendId}
                addressId={currentUser.address.id}
                currentLevel={visibilityLevel}
                defaultLevel={currentUser.address.default_visibility_level ?? 4}
                hasCustomRule={hasCustomVisibility}
                onVisibilityChange={handleVisibilityChange}
              />
            )}
          </div>

          {/* Row 2: Contact buttons */}
          <div className="contact-buttons">
            {friend.phone && (
              <>
                <a
                  href={`tel:${friend.phone}`}
                  className="contact-btn contact-btn-call"
                  title="Appeler"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                  </svg>
                </a>
                <a
                  href={`sms:${friend.phone}`}
                  className="contact-btn contact-btn-sms"
                  title="SMS"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
                  </svg>
                </a>
                <a
                  href={`https://wa.me/${friend.phone.replace(/[^0-9]/g, '')}`}
                  className="contact-btn contact-btn-whatsapp"
                  title="WhatsApp"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </a>
                <a
                  href={`https://signal.me/#p/${friend.phone.replace(/[^0-9+]/g, '')}`}
                  className="contact-btn contact-btn-signal"
                  title="Signal"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M12.006 0C5.397 0 .006 5.391.006 12s5.391 12 11.994 12c6.609 0 12-5.391 12-12s-5.391-12-12-12zm4.94 17.34c-.48.48-1.105.72-1.875.72H8.93c-.77 0-1.395-.24-1.875-.72-.48-.48-.72-1.105-.72-1.875V8.535c0-.77.24-1.395.72-1.875.48-.48 1.105-.72 1.875-.72h6.141c.77 0 1.395.24 1.875.72.48.48.72 1.105.72 1.875v6.93c0 .77-.24 1.395-.72 1.875z"/>
                  </svg>
                </a>
              </>
            )}
            {friend.email && (
              <a
                href={`mailto:${friend.email}`}
                className="contact-btn contact-btn-email"
                title="Email"
                onClick={(e) => e.stopPropagation()}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
              </a>
            )}
          </div>
        </div>

        {/* Back Button */}
        {canGoBack && onBack && (
          <button
            className="btn-back-panel"
            onClick={(e) => { e.stopPropagation(); onBack(); }}
            title="Retour"
          >
            ←
          </button>
        )}
      </div>
      )}

      {/* Scrollable Content - Only visible when expanded */}
      <div
        ref={contentRef}
        className={`profile-content ${panelState === 'minimized' ? 'hidden' : 'visible'}`}
      >

        {/* Header Compact - Desktop only (appears at top of scrollable content) */}
        {isDesktop && (
        <div className="profile-header-compact">
          {/* Row 1: Photo + Name + Visibility */}
          <div className="header-row-main">
            <div className="profile-photo-container-small">
              {friend.profile_photo ? (
                <img
                  src={getPhotoUrl(friend.profile_photo) || undefined}
                  alt={`${friend.first_name} ${friend.last_name}`}
                  className="profile-photo-small"
                />
              ) : (
                <div className="profile-photo-placeholder-small">
                  {friend.first_name[0]}{friend.last_name[0]}
                </div>
              )}
            </div>
            <div className="profile-info-compact">
              <h1 className="profile-name-compact">
                {friend.first_name} {friend.last_name}
              </h1>
              {friend.email && (
                <p className="profile-phone-compact">{friend.email}</p>
              )}
              {friend.phone && (
                <p className="profile-phone-compact">{friend.phone}</p>
              )}
            </div>
            {!isOwnProfile && currentUser?.address?.id && (
              <VisibilityBubble
                friendId={friendId}
                addressId={currentUser.address.id}
                currentLevel={visibilityLevel}
                defaultLevel={currentUser.address.default_visibility_level ?? 4}
                hasCustomRule={hasCustomVisibility}
                onVisibilityChange={handleVisibilityChange}
              />
            )}
          </div>

          {/* Row 2: Contact buttons */}
          <div className="contact-buttons">
            {friend.phone && (
              <>
                <a
                  href={`tel:${friend.phone}`}
                  className="contact-btn contact-btn-call"
                  title="Appeler"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                  </svg>
                </a>
                <a
                  href={`sms:${friend.phone}`}
                  className="contact-btn contact-btn-sms"
                  title="SMS"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
                  </svg>
                </a>
                <a
                  href={`https://wa.me/${friend.phone.replace(/[^0-9]/g, '')}`}
                  className="contact-btn contact-btn-whatsapp"
                  title="WhatsApp"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </a>
                <a
                  href={`https://signal.me/#p/${friend.phone.replace(/[^0-9+]/g, '')}`}
                  className="contact-btn contact-btn-signal"
                  title="Signal"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M12.006 0C5.397 0 .006 5.391.006 12s5.391 12 11.994 12c6.609 0 12-5.391 12-12s-5.391-12-12-12zm4.94 17.34c-.48.48-1.105.72-1.875.72H8.93c-.77 0-1.395-.24-1.875-.72-.48-.48-.72-1.105-.72-1.875V8.535c0-.77.24-1.395.72-1.875.48-.48 1.105-.72 1.875-.72h6.141c.77 0 1.395.24 1.875.72.48.48.72 1.105.72 1.875v6.93c0 .77-.24 1.395-.72 1.875z"/>
                  </svg>
                </a>
              </>
            )}
            {friend.email && (
              <a
                href={`mailto:${friend.email}`}
                className="contact-btn contact-btn-email"
                title="Email"
                onClick={(e) => e.stopPropagation()}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
              </a>
            )}
          </div>
        </div>
        )}

        {/* Personal Note Section - only for friends, not for own profile */}
        {!isOwnProfile && (
        <div className="note-section">
          {isEditingNote ? (
            <div className="note-editor">
              <textarea
                className="note-textarea"
                value={personalNote}
                onChange={(e) => setPersonalNote(e.target.value)}
                placeholder="Écrivez une note personnelle sur cet ami..."
                rows={4}
              />
              <div className="note-actions">
                <button className="btn-save-note" onClick={handleSaveNote} disabled={isSavingNote}>
                  {isSavingNote ? 'Enregistrement...' : 'Enregistrer'}
                </button>
                <button className="btn-cancel-note" onClick={() => setIsEditingNote(false)} disabled={isSavingNote}>
                  Annuler
                </button>
              </div>
            </div>
          ) : (
            <div className="note-display" onClick={() => setIsEditingNote(true)}>
              {personalNote ? (
                <p className="note-text">{personalNote}</p>
              ) : (
                <p className="note-placeholder">Cliquez pour ajouter une note...</p>
              )}
            </div>
          )}
        </div>
        )}

        {/* Lists Section - only for friends, not for own profile */}
        {!isOwnProfile && (
        <div className="groups-section" style={{ position: 'relative' }}>
          {friendLists.length > 0 ? (
            <div className="groups-list">
              {friendLists.map((list) => (
                <span
                  key={list.id}
                  className={`group-badge ${onListClick ? 'clickable' : ''}`}
                  style={{ backgroundColor: list.color }}
                  onClick={() => onListClick?.(list.id)}
                >
                  {list.name}
                </span>
              ))}
              <button
                className="btn-add-to-group"
                title="Ajouter à une liste"
                onClick={() => setShowListsDropdown(!showListsDropdown)}
              >
                +
              </button>
            </div>
          ) : (
            <button
              className="btn-add-to-group-simple"
              onClick={() => setShowListsDropdown(!showListsDropdown)}
            >
              Liste +
            </button>
          )}

          {showListsDropdown && (
            <div ref={listsDropdownRef} className="groups-dropdown">
              <div className="groups-dropdown-header">
                <span>Mes listes</span>
                <button className="btn-close-dropdown" onClick={() => setShowListsDropdown(false)}>
                  ✕
                </button>
              </div>
              <div className="groups-dropdown-content">
                {myLists.length > 0 ? (
                  myLists.map((list) => (
                    <label key={list.id} className="group-checkbox-item">
                      <input
                        type="checkbox"
                        checked={isFriendInList(list.id)}
                        onChange={() => handleToggleList(list.id)}
                        disabled={isUpdatingLists}
                      />
                      <span
                        className="group-checkbox-label"
                        style={{
                          color: list.color || '#4A90E2',
                          fontWeight: isFriendInList(list.id) ? 600 : 400
                        }}
                      >
                        {list.name}
                      </span>
                      <span className="group-members-count">({list.members?.length || 0})</span>
                    </label>
                  ))
                ) : (
                  <div className="no-groups-message">
                    Vous n'avez pas encore de listes. Créez-en une depuis la page Amis !
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        )}

        {/* Official Address */}
        {friend.address && (
          <div className="address-section">
            <h2 className="section-title">🏠 Adresse officielle</h2>
            <div className="address-card-with-button">
              <div
                className="address-card clickable"
                onClick={() =>
                  friend.address?.latitude &&
                  friend.address?.longitude &&
                  handleAddressClickInternal(friend.address.latitude, friend.address.longitude)
                }
              >
                <p className="address-street">{friend.address.street_address}</p>
                <p className="address-city">
                  {friend.address.city}, {friend.address.state && `${friend.address.state}, `}
                  {friend.address.postal_code}
                </p>
                <p className="address-country">{friend.address.country}</p>
              </div>
              {friend.address.latitude && friend.address.longitude && (
                <button
                  className="btn-directions"
                  onClick={() => handleOpenDirections(
                    friend.address!.latitude!,
                    friend.address!.longitude!,
                    `${friend.first_name} ${friend.last_name}`,
                    `${friend.address!.street_address}, ${friend.address!.city}`
                  )}
                  title="Calculer l'itinéraire"
                >
                  <img
                    src={theme === 'dark' ? '/itinéraire-icon-blanc.png' : '/itinéraire-icon-noir.png'}
                    alt="Itinéraire"
                    style={{ width: '18px', height: '18px' }}
                  />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Report User Button - only for other users */}
        {!isOwnProfile && friend && (
          <div className="report-user-section">
            <button
              className="btn-report-user"
              onClick={() => setShowReportModal(true)}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
              </svg>
              Signaler cet utilisateur
            </button>
          </div>
        )}

        {/* Temporary Locations */}
        {tempLocations.length > 0 && (
          <div className="temp-locations-section">
            <h2 className="section-title">📍 Adresses temporaires</h2>
            {tempLocations.map((location) => (
              <div key={location.id} className="temp-location-card clickable"
                onClick={() => handleAddressClickInternal(location.latitude, location.longitude, location.id)}
              >
                <h3 className="location-name">{location.name}</h3>
                {location.description && (
                  <p className="location-description">{location.description}</p>
                )}
                <p className="location-dates">
                  {new Date(location.start_datetime).toLocaleDateString('fr-FR')} -{' '}
                  {new Date(location.end_datetime).toLocaleDateString('fr-FR')}
                </p>
                {location.address && (
                  <p className="location-address">{location.address}</p>
                )}

                {/* Visibility badges - only show for own profile */}
                {isOwnProfile && (
                  <div className="temp-location-visibility">
                    {location.visibility_type === 'all' && (
                      <span className="visibility-badge-compact all">🌍 Tous mes amis</span>
                    )}
                    {location.visibility_type === 'groups' && location.visible_group_ids && location.visible_group_ids.length > 0 && (
                      <span className="visibility-badge-compact groups">
                        👥 {getListNames(location.visible_group_ids)}
                      </span>
                    )}
                    {location.visibility_type === 'friends' && location.visible_friend_ids && location.visible_friend_ids.length > 0 && (
                      <span className="visibility-badge-compact friends">
                        👤 {getFriendNames(location.visible_friend_ids)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Report User Modal */}
      {friend && (
        <UserReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          reportedUserId={friendId}
          reportedUserName={`${friend.first_name} ${friend.last_name}`}
          reportedUserPhoto={friend.profile_photo}
          reportedUserPhone={friend.phone}
        />
      )}
    </div>
  );
};

export default FriendProfilePanel;
